import { useCallback } from 'react';
import { useStore } from '../store/store';
import { WorkflowNode } from '../api/model';

type DrawerContent = 'executions' | 'node_form' | null;

interface UseDrawerReturn {
  isOpen: boolean;
  content: DrawerContent;
  selectedExecutionId: string | null;
  selectedNode: WorkflowNode | null;

  openExecutions: (executionId?: string) => void;
  openNodeForm: (node: WorkflowNode) => void;
  close: () => void;
  toggle: () => void;

  isExecutionsOpen: boolean;
  isNodeFormOpen: boolean;
}

export const useDrawer = (): UseDrawerReturn => {
  const {
    rightDrawerOpen,
    rightDrawerContent,
    selectedExecutionId,
    setRightDrawerOpen,
    setRightDrawerContent,
    setSelectedExecutionId,
    toggleRightDrawer,
    selectedNode,
    setSelectedNode,
  } = useStore();

  const openExecutions = useCallback(
    (executionId?: string) => {
      if (executionId) {
        setSelectedExecutionId(executionId);
      }
      setSelectedNode(null);
      setRightDrawerContent('executions');
      setRightDrawerOpen(true);
    },
    [setSelectedExecutionId, setSelectedNode, setRightDrawerContent, setRightDrawerOpen],
  );

  const openNodeForm = useCallback(
    (node: WorkflowNode) => {
      setSelectedNode(node);
      setSelectedExecutionId(null);
      setRightDrawerContent('node_form');
      setRightDrawerOpen(true);
    },
    [setSelectedNode, setSelectedExecutionId, setRightDrawerContent, setRightDrawerOpen],
  );

  const close = useCallback(() => {
    setRightDrawerOpen(false);
    setRightDrawerContent(null);
    setSelectedExecutionId(null);
    setSelectedNode(null);
  }, [setRightDrawerOpen, setRightDrawerContent, setSelectedExecutionId, setSelectedNode]);

  const toggle = useCallback(() => {
    if (rightDrawerOpen) {
      close();
    } else {
      toggleRightDrawer();
    }
  }, [rightDrawerOpen, close, toggleRightDrawer]);

  return {
    isOpen: rightDrawerOpen,
    content: rightDrawerContent,
    selectedExecutionId,
    selectedNode,

    openExecutions,
    openNodeForm,
    close,
    toggle,

    isExecutionsOpen: rightDrawerOpen && rightDrawerContent === 'executions',
    isNodeFormOpen: rightDrawerOpen && rightDrawerContent === 'node_form' && !!selectedNode,
  };
};
